import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Target,
  Award,
  ArrowRight,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { cn } from '@shared/utils/cn';

interface MatchAnalysisProps {
  jobMatchId: string;
  onAnalyze?: () => void;
}

interface JobMatch {
  id: string;
  job_title: string;
  company_name: string;
  job_description: string;
  match_score: number | null;
  analysis_result: any;
  created_at: string;
}

interface JobAnalysisData {
  id: string;
  strengths: string[] | null;
  weaknesses: string[] | null;
  talking_points: string[] | null;
  strategy_recommendations: string[] | null;
}

export const MatchAnalysis: React.FC<MatchAnalysisProps> = ({
  jobMatchId,
  onAnalyze
}) => {
  const { user } = useAuth();
  const [jobMatch, setJobMatch] = useState<JobMatch | null>(null);
  const [analysis, setAnalysis] = useState<JobAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadJobMatch();
  }, [jobMatchId]);

  const loadJobMatch = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: matchData, error: matchError } = await supabase
        .from('job_matches')
        .select('*')
        .eq('id', jobMatchId)
        .maybeSingle();

      if (matchError) throw matchError;

      if (!matchData) {
        throw new Error('Job match not found');
      }

      setJobMatch(matchData);

      const { data: analysisData, error: analysisError } = await supabase
        .from('job_analysis')
        .select('*')
        .eq('job_match_id', jobMatchId)
        .maybeSingle();

      if (analysisError && analysisError.code !== 'PGRST116') {
        console.warn('Error loading analysis:', analysisError);
      }

      if (analysisData) {
        setAnalysis(analysisData);
      }

      if (user) {
        await supabase
          .from('match_history')
          .insert({
            user_id: user.id,
            job_match_id: jobMatchId,
            interaction_type: 'viewed',
            metadata: { source: 'match_analysis' }
          });
      }
    } catch (error: any) {
      console.error('Error loading job match:', error);
      setError(error.message || 'Failed to load job match');
      toast.error('Failed to load job match data');
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeMatch = async () => {
    if (!user || !jobMatch) return;

    setIsAnalyzing(true);

    try {
      toast.info('Analyzing job match with AI... This may take a moment.');

      const { data: analysisResult, error: aiError } = await supabase.functions.invoke(
        'job-match-analyze',
        {
          body: {
            jobMatchId: jobMatchId,
            userId: user.id,
            jobDescription: jobMatch.job_description
          }
        }
      );

      if (aiError) {
        console.error('AI analysis error:', aiError);
        throw new Error(`AI analysis failed: ${aiError.message}`);
      }

      if (!analysisResult || !analysisResult.success) {
        throw new Error('Failed to generate AI analysis');
      }

      const { data: analysisData, error: analysisError } = await supabase
        .from('job_analysis')
        .select('*')
        .eq('job_match_id', jobMatchId)
        .maybeSingle();

      if (analysisError) throw analysisError;

      const { data: updatedMatch, error: matchError } = await supabase
        .from('job_matches')
        .select('*')
        .eq('id', jobMatchId)
        .maybeSingle();

      if (matchError) throw matchError;

      setAnalysis(analysisData);
      setJobMatch(updatedMatch);

      toast.success('AI analysis completed successfully!');

      if (onAnalyze) {
        onAnalyze();
      }
    } catch (error: any) {
      console.error('Error analyzing match:', error);
      toast.error(error.message || 'Failed to analyze job match');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    return 'Fair Match';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-gray-600">Loading job match analysis...</p>
      </div>
    );
  }

  if (error || !jobMatch) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Match</h3>
        <p className="text-gray-600 mb-4">{error || 'Job match not found'}</p>
        <Button onClick={loadJobMatch} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {jobMatch.job_title}
            </h2>
            <p className="text-lg text-gray-600">{jobMatch.company_name}</p>
          </div>

          {jobMatch.match_score !== null && (
            <div className={cn(
              'text-center px-6 py-3 rounded-lg',
              getScoreColor(jobMatch.match_score)
            )}>
              <div className="text-3xl font-bold">
                {jobMatch.match_score}%
              </div>
              <div className="text-sm font-medium">
                {getScoreLabel(jobMatch.match_score)}
              </div>
            </div>
          )}
        </div>

        {!analysis && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 mb-1">
                  Analysis Not Yet Generated
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  Generate an AI-powered analysis to see your strengths, weaknesses, and personalized recommendations.
                </p>
                <Button
                  onClick={analyzeMatch}
                  disabled={isAnalyzing}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Target className="h-4 w-4" />
                      Generate Analysis
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {analysis && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Your Strengths</h3>
                </div>
                <ul className="space-y-2">
                  {(analysis.strengths || []).map((strength, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <TrendingDown className="h-5 w-5 text-yellow-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Areas to Address</h3>
                </div>
                <ul className="space-y-2">
                  {(analysis.weaknesses || []).map((weakness, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <span>{weakness}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Analysis generated on {new Date(jobMatch.created_at).toLocaleDateString()}
              </p>
              <Button
                onClick={analyzeMatch}
                disabled={isAnalyzing}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Re-analyzing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Re-analyze
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>

      {analysis && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-border/40 p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Award className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Talking Points</h3>
            </div>
            <ul className="space-y-3">
              {(analysis.talking_points || []).map((point, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <ArrowRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-xl border border-border/40 p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Target className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Strategy Recommendations</h3>
            </div>
            <ul className="space-y-3">
              {(analysis.strategy_recommendations || []).map((strategy, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <ArrowRight className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>{strategy}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchAnalysis;
